import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SecurityContext,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpErrorResponse } from '@angular/common/http';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import {
  Observable,
  Subject,
  catchError,
  debounceTime,
  finalize,
  map,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import {
  Meeting,
  MeetingAttendanceType,
  MeetingConfiguration,
  MeetingConfigurationOption,
  MeetingDescriptionType,
  MeetingOrganizerOption,
  MeetingParticipant,
  MeetingParticipantAttendanceStatus,
  MeetingParticipantPayload,
  MeetingPayload,
  MeetingProvider,
  MeetingRecurrenceFrequency,
  MeetingType,
} from 'app/models/meetings/meeting.models';
import { MeetingService } from 'app/services/module-meetings/meeting.service';
import {
  UsuarioBasico,
  UsuariosService,
} from 'app/services/module-config/users/usuarios.service';

export type MeetingModalMode = 'create' | 'edit' | 'view';

export interface MeetingModalInitialData extends Partial<MeetingPayload> {
  external_id?: string;
  join_url?: string | null;
  status?: Meeting['status'];
  sync_status?: Meeting['sync_status'];
}

export type MeetingModalSavedEvent = {
  action: 'created' | 'updated' | 'cancelled' | 'completed' | 'deleted';
  meeting: Meeting;
};

export type MeetingModalClosedEvent =
  | MeetingModalSavedEvent
  | { action: 'closed'; meeting: Meeting | null };

export interface MeetingModalConflictEvent {
  existingMeetingExternalId: string;
}

export interface MeetingParticipantRoleOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-meeting-modal-shared',
  standalone: false,
  templateUrl: './meeting-modal-shared.component.html',
  styleUrls: [
    './meeting-modal-shared.component.css',
    './meeting-modal-shared-detail.component.css',
    './meeting-modal-shared-compact-interview.component.css',
  ],
})
export class MeetingModalSharedComponent implements OnChanges, OnDestroy {
  private static nextInstanceId = 0;

  readonly instanceId = `meeting-modal-${++MeetingModalSharedComponent.nextInstanceId}`;
  readonly titleId = `${this.instanceId}-title`;
  @Input() show = false;
  @Input() meetingExternalId: string | null = null;
  @Input() initialData: MeetingModalInitialData | null = null;
  @Input() mode: MeetingModalMode = 'create';
  @Input() title = 'Programar reunión';
  @Input() contextLabel = '';
  @Input() organizerOptions: MeetingOrganizerOption[] = [];
  @Input() lockedParticipantEmails: string[] = [];
  @Input() allowBackdropClose = false;
  @Input() confirmDiscardChanges = true;
  @Input() closeOnEscape = true;
  @Input() allowCancel = true;
  @Input() allowComplete = false;
  @Input() compactInterviewMode = false;
  readonly categoryOptions = [
    'Reunión',
    'Seguimiento',
    'Pendiente',
    'Proyecto',
    'Capacitación',
    'Entrevista',
    'Personal',
    'Otro',
  ];

  readonly reminderOptions = [
    { value: 0, label: 'Sin recordatorio' },
    { value: 5, label: '5 minutos antes' },
    { value: 10, label: '10 minutos antes' },
    { value: 15, label: '15 minutos antes' },
    { value: 30, label: '30 minutos antes' },
    { value: 60, label: '1 hora antes' },
    { value: 120, label: '2 horas antes' },
    { value: 1440, label: '1 día antes' },
  ];

  readonly recurrenceOptions: Array<{
    value: MeetingRecurrenceFrequency;
    label: string;
  }> = [
    { value: 'DAILY', label: 'Cada día' },
    { value: 'WEEKLY', label: 'Cada semana' },
    { value: 'MONTHLY', label: 'Cada mes' },
  ];

  readonly allParticipantRoleOptions: MeetingParticipantRoleOption[] = [
    { value: 'CANDIDATE', label: 'Candidato' },
    { value: 'INTERVIEWER', label: 'Entrevistador' },
    { value: 'HR', label: 'Talento Humano' },
    { value: 'SUPERVISOR', label: 'Supervisor' },
    { value: 'GUEST', label: 'Invitado' },
    { value: 'ZIVO_USER', label: 'Usuario Zivo' },
  ];
  @Input() participantRoleOptions: MeetingParticipantRoleOption[] = [
    { value: 'GUEST', label: 'Invitado' },
    { value: 'ZIVO_USER', label: 'Usuario Zivo' },
  ];

  @Output() saved = new EventEmitter<MeetingModalSavedEvent>();
  @Output() closed = new EventEmitter<MeetingModalClosedEvent>();
  @Output() conflict = new EventEmitter<MeetingModalConflictEvent>();

  @ViewChild('dialogPanel') dialogPanel?: ElementRef<HTMLElement>;
  @ViewChild('descriptionEditor')
  descriptionEditor?: ElementRef<HTMLTextAreaElement>;

  readonly form: FormGroup;
  configuration: MeetingConfiguration = this.fallbackConfiguration();
  currentMeeting: Meeting | null = null;
  activeMode: MeetingModalMode = 'create';
  resolvedExternalId: string | null = null;

  loading = false;
  saving = false;
  attendanceLoading = false;
  attendanceUpdatingExternalId: string | null = null;
  configurationLoading = false;
  errorMessage = '';
  attendanceMessage = '';
  participantError = '';
  conflictExternalId: string | null = null;
  confirmingClose = false;
  lifecycleAction: 'cancel' | 'complete' | 'delete' | null = null;
  cancellationReason = '';
  showDescriptionPreview = false;
  maximized = false;
  userSearch = '';
  userSearchOpen = false;
  searchingUsers = false;
  userSearchError = '';
  userSearchResults: UsuarioBasico[] = [];
  joinUrlCopied = false;

  private previousBodyOverflow: string | null = null;
  private focusTimer: number | null = null;
  private previouslyFocusedElement: HTMLElement | null = null;
  private meetingRequestSequence = 0;
  private readonly userSearchSubject = new Subject<string>();
  private readonly destroySubject = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly meetingService: MeetingService,
    private readonly usuariosService: UsuariosService,
    private readonly sanitizer: DomSanitizer,
  ) {
    const defaults = this.defaultDates();
    this.form = this.fb.group({
      subject: ['', [Validators.required, Validators.maxLength(255)]],
      description_type: ['HTML' as MeetingDescriptionType, Validators.required],
      description: [''],
      start_at: [defaults.start, Validators.required],
      end_at: [defaults.end, Validators.required],
      timezone: ['America/Guayaquil', Validators.required],
      meeting_type: ['ONLINE' as MeetingType, Validators.required],
      provider: ['MICROSOFT_TEAMS' as MeetingProvider, Validators.required],
      organizer_user_id: [null as number | null],
      organizer_user_external_id: [null as string | null],
      location: [''],
      is_all_day: [false],
      all_day_date: [''],
      reminder_minutes: [15],
      category: ['Reunión'],
      recurrence_frequency: ['NONE' as 'NONE' | MeetingRecurrenceFrequency],
      recurrence_interval: [1],
      recurrence_end_date: [''],
      participants: this.fb.array([]),
    });
    this.configureUserSearch();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['show']) {
      if (this.show) {
        this.openModal();
      } else {
        this.meetingRequestSequence += 1;
        this.restoreBodyScroll();
      }
      return;
    }

    if (
      this.show &&
      (changes['meetingExternalId'] || changes['initialData'] || changes['mode'])
    ) {
      this.openModal();
    }
  }

  ngOnDestroy(): void {
    this.meetingRequestSequence += 1;
    this.destroySubject.next();
    this.destroySubject.complete();
    this.restoreBodyScroll();
    this.clearFocusTimer();
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.show && this.userSearchOpen) {
      this.userSearchOpen = false;
      return;
    }
    if (this.show && this.closeOnEscape && !this.saving) {
      this.requestClose();
    }
  }

  @HostListener('document:keydown.tab', ['$event'])
  trapFocus(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;

    if (!this.show || !this.dialogPanel) return;

    const scope =
      this.dialogPanel.nativeElement.querySelector<HTMLElement>(
        '.meeting-confirm-overlay',
      ) || this.dialogPanel.nativeElement;
    const focusable = Array.from(
      scope.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      ),
    ).filter(
      (element) =>
        element.offsetParent !== null && element.getAttribute('aria-hidden') !== 'true',
    );
    if (focusable.length === 0) {
      keyboardEvent.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (keyboardEvent.shiftKey && (active === first || !scope.contains(active))) {
      keyboardEvent.preventDefault();
      last.focus();
    } else if (!keyboardEvent.shiftKey && active === last) {
      keyboardEvent.preventDefault();
      first.focus();
    }
  }

  get participants(): FormArray {
    return this.form.get('participants') as FormArray;
  }

  get isReadOnly(): boolean {
    return this.activeMode === 'view';
  }

  get isEditing(): boolean {
    return this.activeMode === 'edit';
  }

  get isMeetingClosed(): boolean {
    return Boolean(
      this.currentMeeting && this.currentMeeting.status !== 'SCHEDULED',
    );
  }

  get isSeries(): boolean {
    return Boolean(
      this.currentMeeting?.recurrence_pattern?.frequency ||
        this.form.get('recurrence_frequency')?.value !== 'NONE',
    );
  }

  get isSeriesExpired(): boolean {
    const meeting = this.currentMeeting;
    const endDate = String(
      meeting?.recurrence_pattern?.end_date || "",
    ).slice(0, 10);
    if (!meeting || !endDate) return false;
    try {
      const today = formatInTimeZone(
        new Date(),
        meeting.timezone || 'America/Guayaquil',
        'yyyy-MM-dd',
      );
      return endDate < today;
    } catch {
      return false;
    }
  }

  get isRescheduleOnly(): boolean {
    const meeting = this.currentMeeting;
    return Boolean(
      meeting?.status === 'SCHEDULED' &&
        !meeting.recurrence_pattern?.frequency &&
        new Date(meeting.end_at).getTime() <= Date.now(),
    );
  }

  get canEditMeetingDetails(): boolean {
    return (
      !this.isReadOnly &&
      !this.isRescheduleOnly &&
      !this.isSeriesExpired &&
      (!this.currentMeeting || this.currentMeeting.permissions?.can_manage === true)
    );
  }

  get modalHeading(): string {
    if (this.isReadOnly) return this.isSeries ? 'Detalle de la serie' : 'Detalle de la reunión';
    if (this.isSeriesExpired) return 'Serie finalizada';
    if (this.isRescheduleOnly) return 'Reprogramar reunión expirada';
    if (this.isEditing) return this.isSeries ? 'Editar serie' : 'Editar reunión';
    return this.title || 'Programar reunión';
  }

  get submitLabel(): string {
    if (this.isRescheduleOnly) return 'Reprogramar reunión';
    if (this.isEditing && this.isSeries) return 'Guardar serie';
    return this.isEditing ? 'Guardar cambios' : this.isSeries ? 'Crear serie' : 'Programar reunión';
  }

  get isAllDay(): boolean {
    return Boolean(this.form.get('is_all_day')?.value);
  }

  get isRecurring(): boolean {
    return this.form.get('recurrence_frequency')?.value !== 'NONE';
  }

  get descriptionType(): MeetingDescriptionType {
    return (this.form.get('description_type')?.value ||
      'HTML') as MeetingDescriptionType;
  }

  get descriptionPreview(): string {
    const description = String(this.form.get('description')?.value || '');
    return (
      this.sanitizer.sanitize(SecurityContext.HTML, description) ||
      '<p>Sin descripción.</p>'
    );
  }

  get safeJoinUrl(): string | null {
    if (
      this.currentMeeting &&
      (this.currentMeeting.status !== 'SCHEDULED' || this.isRescheduleOnly)
    ) {
      return null;
    }
    const value = this.currentMeeting?.join_url || this.initialData?.join_url;
    if (!value) return null;

    try {
      const parsed = new URL(value);
      return parsed.protocol === 'https:' ? parsed.href : null;
    } catch {
      return null;
    }
  }

  get displayJoinUrl(): string | null {
    const value = this.currentMeeting?.join_url || this.initialData?.join_url;
    if (!value) return null;

    try {
      const parsed = new URL(value);
      return parsed.protocol === 'https:' ? parsed.href : null;
    } catch {
      return null;
    }
  }

  get hasOrganizerOptions(): boolean {
    return this.organizerOptions.length > 0;
  }

  get canSubmit(): boolean {
    return (
      !this.isReadOnly &&
      !this.isMeetingClosed &&
      !this.isSeriesExpired &&
      !this.loading &&
      !this.saving
    );
  }

  get canCancelMeeting(): boolean {
    return Boolean(
      this.allowCancel &&
        this.currentMeeting?.permissions?.can_manage === true &&
        this.currentMeeting?.external_id &&
        this.currentMeeting.status === 'SCHEDULED' &&
        !this.isRescheduleOnly &&
        !this.isSeriesExpired &&
        !this.saving,
    );
  }

  get canDeleteMeeting(): boolean {
    return Boolean(
      this.currentMeeting?.permissions?.can_manage === true &&
        this.currentMeeting?.external_id &&
        !this.saving,
    );
  }

  get lifecycleTitle(): string {
    if (this.lifecycleAction === 'delete') {
      return this.isSeries ? '¿Eliminar toda la serie?' : '¿Eliminar este evento?';
    }
    if (this.lifecycleAction === 'cancel') {
      return this.isSeries ? '¿Cancelar toda la serie?' : '¿Cancelar esta reunión?';
    }
    return '¿Marcar la reunión como completada?';
  }

  get lifecycleDescription(): string {
    if (this.lifecycleAction === 'delete') {
      return this.isSeries
        ? 'Se eliminarán todas las ocurrencias de la serie en Microsoft y dejará de aparecer en la agenda de ZIVO.'
        : 'El evento se eliminará de Microsoft y dejará de aparecer en la agenda de ZIVO.';
    }
    if (this.lifecycleAction === 'cancel') {
      return this.isSeries
        ? 'Se cancelará toda la serie en Microsoft y se notificará a los participantes.'
        : 'Se cancelará también la invitación del proveedor y se notificará a los participantes.';
    }
    return 'La reunión quedará cerrada y ya no podrá reprogramarse.';
  }

  get lifecycleConfirmLabel(): string {
    if (this.lifecycleAction === 'delete') {
      return this.isSeries ? 'Sí, eliminar serie' : 'Sí, eliminar evento';
    }
    if (this.lifecycleAction === 'cancel') {
      return this.isSeries ? 'Sí, cancelar serie' : 'Sí, cancelar reunión';
    }
    return 'Sí, completar';
  }

  get canCompleteMeeting(): boolean {
    return Boolean(
      this.allowComplete &&
        this.currentMeeting?.permissions?.can_manage === true &&
        this.currentMeeting?.external_id &&
        this.currentMeeting.status === 'SCHEDULED' &&
        !this.isRescheduleOnly &&
        !this.isSeriesExpired &&
        !this.saving,
    );
  }

  get canSyncAttendance(): boolean {
    const meeting = this.currentMeeting;
    return Boolean(
        meeting?.external_id &&
        meeting.permissions?.can_manage === true &&
        meeting.provider === 'MICROSOFT_TEAMS' &&
        new Date(meeting.end_at).getTime() <= Date.now() &&
        !this.loading &&
        !this.saving &&
        !this.attendanceLoading,
    );
  }

  get attendanceSyncLabel(): string {
    const status = this.currentMeeting?.attendance_sync_status;
    if (status === 'SYNCED') return 'Asistencia actualizada';
    if (status === 'ERROR') return 'Error al consultar asistencia';
    if (status === 'PENDING') return 'Consultando asistencia';
    return 'Asistencia aún no consultada';
  }

  get originalOrganizerEmail(): string {
    return String(
      this.currentMeeting?.organizer_email ||
        this.currentMeeting?.organizer?.correo ||
        this.currentMeeting?.organizer?.email ||
        '',
    ).trim();
  }

  get defaultParticipantRole(): string {
    return this.participantRoleOptions.some((option) => option.value === 'GUEST')
      ? 'GUEST'
      : this.participantRoleOptions[0]?.value || 'GUEST';
  }

  get internalUserParticipantRole(): string {
    return this.participantRoleOptions.some(
      (option) => option.value === 'ZIVO_USER',
    )
      ? 'ZIVO_USER'
      : this.defaultParticipantRole;
  }

  participantRoleOptionsFor(index: number): MeetingParticipantRoleOption[] {
    const currentRole = String(
      this.participants.at(index)?.get('participant_role')?.value || '',
    ).trim().toUpperCase();
    const availableOptions = this.isReadOnly
      ? this.allParticipantRoleOptions
      : this.participantRoleOptions;
    if (
      !currentRole ||
      availableOptions.some((option) => option.value === currentRole)
    ) {
      return availableOptions;
    }
    const historical = this.allParticipantRoleOptions.find(
      (option) => option.value === currentRole,
    ) || { value: currentRole, label: currentRole };
    return [historical, ...availableOptions];
  }

  isParticipantRoleLocked(index: number): boolean {
    if (this.isParticipantLocked(index)) return true;
    const currentRole = String(
      this.participants.at(index)?.get('participant_role')?.value || '',
    ).trim().toUpperCase();
    return Boolean(
      currentRole &&
        !this.participantRoleOptions.some(
          (option) => option.value === currentRole,
        ),
    );
  }

  get providers(): MeetingConfigurationOption<MeetingProvider>[] {
    return this.configuration.providers.filter(
      (option) =>
        option.enabled !== false &&
        (this.activeMode !== 'create' || option.creatable !== false),
    );
  }

  get meetingTypes(): MeetingConfigurationOption<MeetingType>[] {
    return this.configuration.meeting_types.filter(
      (option) => option.enabled !== false,
    );
  }

  backdropClicked(event: MouseEvent): void {
    if (event.target === event.currentTarget && this.allowBackdropClose) {
      this.requestClose();
    }
  }

  requestClose(): void {
    if (this.saving) return;

    if (
      this.confirmDiscardChanges &&
      !this.isReadOnly &&
      this.form.dirty &&
      !this.confirmingClose
    ) {
      this.confirmingClose = true;
      this.focusConfirmation();
      return;
    }

    this.emitClosed();
  }

  continueEditing(): void {
    this.confirmingClose = false;
  }

  discardAndClose(): void {
    this.confirmingClose = false;
    this.emitClosed();
  }

  requestLifecycleAction(action: 'cancel' | 'complete' | 'delete'): void {
    if (this.saving) return;
    this.lifecycleAction = action;
    this.cancellationReason = '';
    this.focusConfirmation();
  }

  cancelLifecycleAction(): void {
    this.lifecycleAction = null;
    this.cancellationReason = '';
  }

  confirmLifecycleAction(): void {
    const externalId = this.currentMeeting?.external_id;
    const action = this.lifecycleAction;
    if (!externalId || !action || this.saving) return;

    this.saving = true;
    this.errorMessage = '';
    const operation =
      action === 'cancel'
        ? this.meetingService.cancel(
            externalId,
            this.cancellationReason.trim() || undefined,
          )
        : action === 'delete'
          ? this.meetingService.remove(externalId)
          : this.meetingService.complete(externalId);

    operation.pipe(finalize(() => (this.saving = false))).subscribe({
      next: (meeting) => {
        this.lifecycleAction = null;

        if (action === 'delete') {
          const event: MeetingModalSavedEvent = {
            action: 'deleted',
            meeting,
          };
          this.saved.emit(event);
          this.closed.emit(event);
          return;
        }

        this.currentMeeting = meeting;
        if (meeting.status !== 'SCHEDULED') {
          this.form.disable({ emitEvent: false });
        }
        const event: MeetingModalSavedEvent = {
          action: action === 'cancel' ? 'cancelled' : 'completed',
          meeting,
        };
        this.saved.emit(event);
        if (meeting.sync_status === 'ERROR') {
          this.errorMessage =
            meeting.sync_error ||
            'El cambio se guardó en ZIVO, pero Microsoft no confirmó la operación. Reintente la sincronización antes de cerrar.';
          return;
        }
        this.closed.emit(event);
      },
      error: (error: unknown) => {
        this.lifecycleAction = null;
        this.handleRequestError(error);
      },
    });
  }

  toggleMaximized(): void {
    this.maximized = !this.maximized;
  }

  setDescriptionType(type: MeetingDescriptionType): void {
    if (!this.canEditMeetingDetails) return;
    this.form.get('description_type')?.setValue(type);
    this.form.get('description_type')?.markAsDirty();
    if (type === 'TEXT') this.showDescriptionPreview = false;
  }

  toggleDescriptionPreview(): void {
    this.showDescriptionPreview = !this.showDescriptionPreview;
  }

  insertHtml(openingTag: string, closingTag: string): void {
    if (!this.canEditMeetingDetails || this.descriptionType !== 'HTML') return;

    const control = this.form.get('description');
    const textarea = this.descriptionEditor?.nativeElement;
    const current = String(control?.value || '');
    const start = textarea?.selectionStart ?? current.length;
    const end = textarea?.selectionEnd ?? current.length;
    const selected = current.slice(start, end);
    const value =
      current.slice(0, start) +
      openingTag +
      selected +
      closingTag +
      current.slice(end);

    control?.setValue(value);
    control?.markAsDirty();

    window.setTimeout(() => {
      textarea?.focus();
      const position = start + openingTag.length + selected.length;
      textarea?.setSelectionRange(position, position);
    });
  }

  setRecurrenceEnabled(enabled: boolean): void {
    if (!this.canEditMeetingDetails) return;

    if (!enabled) {
      this.form.patchValue({
        recurrence_frequency: 'NONE',
        recurrence_interval: 1,
        recurrence_end_date: '',
      });
      this.form.markAsDirty();
      return;
    }

    const startDate = this.effectiveStartDateKey();
    const end = new Date(`${startDate}T12:00:00`);
    end.setMonth(end.getMonth() + 1);

    this.form.patchValue({
      recurrence_frequency:
        this.form.get('recurrence_frequency')?.value === 'NONE'
          ? 'WEEKLY'
          : this.form.get('recurrence_frequency')?.value,
      recurrence_interval: Math.max(
        Number(this.form.get('recurrence_interval')?.value || 1),
        1,
      ),
      recurrence_end_date:
        this.form.get('recurrence_end_date')?.value ||
        this.localDateKey(end),
    });
    this.form.markAsDirty();
  }

  onAllDayChanged(): void {
    if (!this.canEditMeetingDetails) return;
    const enabled = Boolean(this.form.get('is_all_day')?.value);
    if (enabled && !this.form.get('all_day_date')?.value) {
      this.form.get('all_day_date')?.setValue(this.effectiveStartDateKey());
    }
    this.form.markAsDirty();
  }

  onMeetingTypeChanged(): void {
    const type = this.form.get('meeting_type')?.value as MeetingType;
    const providerControl = this.form.get('provider');
    const locationControl = this.form.get('location');

    if (type === 'PRESENTIAL') {
      providerControl?.setValue('NONE');
      locationControl?.addValidators(Validators.required);
    } else {
      if (providerControl?.value === 'NONE') {
        providerControl.setValue(this.configuration.default_provider);
      }
      locationControl?.removeValidators(Validators.required);
    }

    locationControl?.updateValueAndValidity();
  }

  setTeamsEnabled(enabled: boolean): void {
    this.form.patchValue({
      meeting_type: 'ONLINE',
      provider: enabled ? 'MICROSOFT_TEAMS' : 'NONE',
      location: '',
    });
    this.form.markAsDirty();
  }

  onCompactInterviewStartChanged(): void {
    if (!this.compactInterviewMode) return;
    const startValue = this.form.get('start_at')?.value;
    const startAt = new Date(startValue).getTime();
    if (!Number.isFinite(startAt)) return;

    const endAt = new Date(startAt + 60 * 60 * 1000);
    const localEnd = new Date(endAt.getTime() - endAt.getTimezoneOffset() * 60_000)
      .toISOString()
      .slice(0, 16);
    this.form.get('end_at')?.setValue(localEnd);
  }

  addParticipant(participant?: Partial<MeetingParticipantPayload>): void {
    if (!this.canEditMeetingDetails) return;
    this.participants.push(this.createParticipantGroup(participant));
    this.participants.markAsDirty();
    this.participantError = '';
  }

  removeParticipant(index: number): void {
    if (!this.canEditMeetingDetails || this.isParticipantLocked(index)) return;
    this.participants.removeAt(index);
    this.participants.markAsDirty();
    this.participantError = '';
  }

  isParticipantLocked(index: number): boolean {
    const email = String(this.participants.at(index)?.get('email')?.value || '')
      .trim()
      .toLowerCase();
    return this.lockedParticipantEmails.some(
      (lockedEmail) => lockedEmail.trim().toLowerCase() === email,
    );
  }

  participantTrackBy(index: number): number {
    return index;
  }

  onUserSearchChanged(value: string): void {
    this.userSearch = value;
    this.userSearchOpen = true;
    this.userSearchError = '';
    this.userSearchSubject.next(value);
  }

  openUserSearch(): void {
    this.userSearchOpen = true;
    if (this.userSearch.trim().length >= 2) {
      this.userSearchSubject.next(this.userSearch);
    }
  }

  closeUserSearch(): void {
    window.setTimeout(() => {
      this.userSearchOpen = false;
    }, 140);
  }

  isBasicUserAlreadyParticipant(user: UsuarioBasico): boolean {
    const externalId = String(user.external_id || '').trim().toLowerCase();
    const email = String(user.correo || '').trim().toLowerCase();
    return this.participants.controls.some((control) => {
      const participantExternalId = String(
        control.get('entity_external_id')?.value || '',
      )
        .trim()
        .toLowerCase();
      const participantEmail = String(control.get('email')?.value || '')
        .trim()
        .toLowerCase();
      return (
        (externalId && participantExternalId === externalId) ||
        (email && participantEmail === email)
      );
    });
  }

  basicUserDisabledReason(user: UsuarioBasico): string {
    if (!String(user.correo || '').trim()) return 'Sin correo registrado';
    if (this.isBasicUserAlreadyParticipant(user)) return 'Ya agregado';
    return '';
  }

  selectBasicUser(user: UsuarioBasico): void {
    const disabledReason = this.basicUserDisabledReason(user);
    if (disabledReason) {
      this.participantError =
        disabledReason === 'Ya agregado'
          ? 'El usuario seleccionado ya está incluido en la reunión.'
          : 'Este usuario no tiene un correo registrado y no puede recibir la invitación.';
      return;
    }

    this.addParticipant({
      entity_type: 'USER',
      entity_external_id: user.external_id,
      name: `${user.nombre || ''} ${user.apellidos || ''}`
        .replace(/\s+/g, ' ')
        .trim(),
      email: String(user.correo).trim().toLowerCase(),
      participant_role: this.internalUserParticipantRole,
      attendance_type: 'REQUIRED',
    });
    this.userSearch = '';
    this.userSearchResults = [];
    this.userSearchOpen = false;
    this.userSearchError = '';
  }

  submit(): void {
    if (!this.canSubmit) return;

    this.errorMessage = '';
    this.participantError = '';
    this.conflictExternalId = null;
    this.form.markAllAsTouched();

    if (!this.validateDates() || !this.validateParticipants()) return;
    if (this.form.invalid) {
      this.errorMessage = 'Revise los campos obligatorios antes de continuar.';
      this.focusFirstInvalidControl();
      return;
    }

    const payload = this.buildPayload();
    this.saving = true;

    const operation = this.isRescheduleOnly
      ? this.meetingService.reschedule(this.resolvedExternalId!, {
          start_at: payload.start_at,
          end_at: payload.end_at,
          timezone: payload.timezone,
        })
      : this.isEditing
        ? this.updateMeeting(payload)
        : this.meetingService.create(payload);

    operation.pipe(finalize(() => (this.saving = false))).subscribe({
      next: (meeting) => {
        this.currentMeeting = meeting;
        this.form.markAsPristine();
        const event: MeetingModalSavedEvent = {
          action: this.isEditing || this.isRescheduleOnly ? 'updated' : 'created',
          meeting,
        };
        this.saved.emit(event);
        if (meeting.sync_status === 'ERROR') {
          this.errorMessage =
            meeting.sync_error ||
            'La reunión se guardó en ZIVO, pero Microsoft no confirmó la sincronización. Puede reintentarla sin duplicar la invitación.';
          return;
        }
        this.closed.emit(event);
      },
      error: (error: unknown) => this.handleRequestError(error),
    });
  }

  openExistingConflict(): void {
    if (!this.conflictExternalId) return;
    const externalId = this.conflictExternalId;
    this.conflict.emit({ existingMeetingExternalId: externalId });
    this.activeMode = this.mode === 'view' ? 'view' : 'edit';
    this.loadMeeting(externalId);
  }

  openJoinUrl(): void {
    if (!this.safeJoinUrl) return;
    window.open(this.safeJoinUrl, '_blank', 'noopener,noreferrer');
  }

  async copyJoinUrl(): Promise<void> {
    if (!this.displayJoinUrl) return;

    try {
      await navigator.clipboard.writeText(this.displayJoinUrl);
      this.joinUrlCopied = true;
      window.setTimeout(() => (this.joinUrlCopied = false), 1800);
    } catch {
      this.joinUrlCopied = false;
    }
  }

  retrySync(): void {
    const externalId = this.currentMeeting?.external_id;
    if (
      !externalId ||
      this.saving ||
      this.currentMeeting?.permissions?.can_manage !== true
    ) return;

    this.saving = true;
    this.errorMessage = '';
    this.meetingService
      .retrySync(externalId)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (meeting) => {
          this.currentMeeting = meeting;
          this.errorMessage =
            meeting.sync_status === 'ERROR'
              ? meeting.sync_error || 'Microsoft no confirmó la sincronización.'
              : '';
        },
        error: (error: unknown) => this.handleRequestError(error),
      });
  }

  syncAttendance(): void {
    const externalId = this.currentMeeting?.external_id;
    if (!externalId || !this.canSyncAttendance) return;
    if (this.form.dirty) {
      this.errorMessage =
        'Guarde primero los cambios de la reunión antes de actualizar la asistencia.';
      return;
    }

    this.attendanceLoading = true;
    this.attendanceMessage = '';
    this.errorMessage = '';
    this.meetingService
      .syncAttendance(externalId)
      .pipe(finalize(() => (this.attendanceLoading = false)))
      .subscribe({
        next: (meeting) => {
          this.currentMeeting = meeting;
          this.resetForm(meeting);
          if (meeting.attendance_sync_status === 'ERROR') {
            this.errorMessage =
              meeting.attendance_sync_error ||
              'Microsoft no pudo entregar el reporte de asistencia.';
          } else if (meeting.attendance_sync_status === 'NOT_AVAILABLE') {
            this.attendanceMessage =
              'Teams todavía está generando el reporte. Puede volver a consultarlo en unos minutos.';
          } else {
            this.attendanceMessage =
              'Se actualizaron las horas reales de ingreso y salida de los participantes.';
          }
        },
        error: (error: unknown) => this.handleRequestError(error),
      });
  }

  canRegisterParticipantAttendance(index: number): boolean {
    const meeting = this.currentMeeting;
    const participant = this.participants.at(index);
    if (
      !meeting?.external_id ||
      !participant?.get('external_id')?.value ||
      meeting.status === 'CANCELLED' ||
      new Date(meeting.start_at).getTime() > Date.now()
    ) {
      return false;
    }
    return Boolean(
      meeting.permissions?.can_manage || participant.get('is_current_user')?.value,
    );
  }

  isParticipantAttendanceUpdating(index: number): boolean {
    return Boolean(
      this.attendanceUpdatingExternalId &&
        this.attendanceUpdatingExternalId ===
          String(this.participants.at(index)?.get('external_id')?.value || ''),
    );
  }

  registerParticipantAttendance(
    index: number,
    attendanceStatus: MeetingParticipantAttendanceStatus,
  ): void {
    if (!this.canRegisterParticipantAttendance(index)) return;
    const meeting = this.currentMeeting!;
    const participant = this.participants.at(index);
    const participantExternalId = String(
      participant.get('external_id')?.value || '',
    );
    const isCreator = meeting.permissions?.can_manage === true;
    this.attendanceUpdatingExternalId = participantExternalId;
    this.attendanceMessage = '';
    this.errorMessage = '';
    const operation = isCreator
      ? this.meetingService.updateParticipantAttendance(
          meeting.external_id,
          participantExternalId,
          attendanceStatus,
        )
      : this.meetingService.registerOwnAttendance(
          meeting.external_id,
          attendanceStatus,
        );
    operation
      .pipe(finalize(() => (this.attendanceUpdatingExternalId = null)))
      .subscribe({
        next: (updatedMeeting) => {
          this.currentMeeting = updatedMeeting;
          this.resetForm(updatedMeeting);
          this.attendanceMessage = isCreator
            ? 'La asistencia del participante quedó registrada manualmente.'
            : 'Tu asistencia quedó registrada correctamente.';
        },
        error: (error: unknown) => this.handleRequestError(error),
      });
  }

  statusLabel(status?: Meeting['status']): string {
    const labels: Record<Meeting['status'], string> = {
      SCHEDULED: 'Programada',
      COMPLETED: 'Completada',
      CANCELLED: 'Cancelada',
    };
    return status ? labels[status] : 'Borrador';
  }

  syncStatusLabel(status?: Meeting['sync_status']): string {
    const labels: Record<Meeting['sync_status'], string> = {
      PENDING: 'Sincronización pendiente',
      SYNCED: 'Sincronizada',
      ERROR: 'Error de sincronización',
    };
    return status ? labels[status] : 'Sin sincronizar';
  }

  responseStatusLabel(status?: MeetingParticipant['response_status']): string {
    const labels: Record<string, string> = {
      NONE: 'Sin respuesta',
      NOT_RESPONDED: 'Sin respuesta',
      ACCEPTED: 'Aceptó',
      TENTATIVELY_ACCEPTED: 'Tentativa',
      DECLINED: 'Rechazó',
    };
    return status ? labels[status] || status : 'Sin respuesta';
  }

  attendanceStatusLabel(
    status?: MeetingParticipant['attendance_status'],
  ): string {
    const labels: Record<string, string> = {
      NOT_AVAILABLE: 'Sin reporte',
      PRESENT: 'Ingresó a la reunión',
      ABSENT: 'Sin registro de ingreso',
    };
    return status ? labels[status] || status : 'Sin reporte';
  }

  formatAttendanceDate(value?: string | null): string {
    if (!value) return '—';
    try {
      return formatInTimeZone(
        value,
        this.currentMeeting?.timezone || 'America/Guayaquil',
        'dd/MM/yyyy HH:mm',
      );
    } catch {
      return '—';
    }
  }

  formatAttendanceDuration(seconds?: number | null): string {
    const total = Math.max(Number(seconds || 0), 0);
    if (!total) return '0 min';
    const hours = Math.floor(total / 3600);
    const minutes = Math.max(Math.round((total % 3600) / 60), 1);
    return hours ? `${hours} h ${minutes} min` : `${minutes} min`;
  }

  private openModal(): void {
    this.meetingRequestSequence += 1;
    this.lockBodyScroll();
    this.resetVisualState();
    this.activeMode = this.resolveMode();
    this.resolvedExternalId =
      this.meetingExternalId || this.initialData?.external_id || null;
    this.loadConfiguration();

    if (this.resolvedExternalId) {
      this.loadMeeting(this.resolvedExternalId);
    } else {
      this.currentMeeting = null;
      this.resetForm(this.initialData || {});
      this.scheduleInitialFocus();
    }
  }

  private resolveMode(): MeetingModalMode {
    if (this.mode === 'view') return 'view';
    if (this.meetingExternalId || this.initialData?.external_id) return 'edit';
    return this.mode;
  }

  private loadMeeting(externalId: string): void {
    const requestSequence = ++this.meetingRequestSequence;
    this.loading = true;
    this.errorMessage = '';
    this.conflictExternalId = null;
    this.meetingService
      .getByExternalId(externalId)
      .pipe(
        finalize(() => {
          if (requestSequence === this.meetingRequestSequence) {
            this.loading = false;
          }
        }),
      )
      .subscribe({
        next: (meeting) => {
          if (
            requestSequence !== this.meetingRequestSequence ||
            !this.show
          ) {
            return;
          }
          this.resolvedExternalId = meeting.external_id;
          this.currentMeeting = meeting;
          if (meeting.permissions?.can_manage !== true) {
            this.activeMode = 'view';
          }
          this.resetForm(meeting);
          this.scheduleInitialFocus();
        },
        error: (error: unknown) => {
          if (
            requestSequence !== this.meetingRequestSequence ||
            !this.show
          ) {
            return;
          }
          this.handleRequestError(error);
        },
      });
  }

  private loadConfiguration(): void {
    this.configurationLoading = true;
    this.meetingService
      .getConfiguration()
      .pipe(
        catchError(() => of(this.fallbackConfiguration())),
        finalize(() => (this.configurationLoading = false)),
      )
      .subscribe((configuration) => {
        this.configuration = this.normalizeConfiguration(configuration);
        if (
          this.activeMode === 'create' &&
          !this.resolvedExternalId &&
          this.form.pristine
        ) {
          const defaults: Record<string, string> = {};
          if (!this.initialData?.provider) {
            defaults['provider'] = this.configuration.default_provider;
          }
          if (!this.initialData?.meeting_type) {
            defaults['meeting_type'] = this.configuration.default_meeting_type;
          }
          if (!this.initialData?.timezone) {
            defaults['timezone'] = this.configuration.default_timezone;
          }
          this.form.patchValue(defaults, { emitEvent: false });
          this.onMeetingTypeChanged();
          this.form.markAsPristine();
        }
      });
  }

  private resetForm(data: MeetingModalInitialData | Meeting): void {
    const timezone = data.timezone || this.configuration.default_timezone;
    const defaults = this.defaultDates();
    const participants = data.participants || [];
    this.showDescriptionPreview = Boolean(
      data.description && (data.description_type || 'HTML') === 'HTML',
    );

    this.form.reset({
      subject: data.subject || '',
      description_type: data.description_type || 'HTML',
      description: data.description || '',
      start_at: data.start_at
        ? this.toLocalDateTime(data.start_at, timezone)
        : defaults.start,
      end_at: data.end_at
        ? this.toLocalDateTime(data.end_at, timezone)
        : defaults.end,
      timezone,
      meeting_type:
        data.meeting_type || this.configuration.default_meeting_type,
      provider: data.provider || this.configuration.default_provider,
      organizer_user_id: data.organizer_user_id ?? null,
      organizer_user_external_id:
        data.organizer_user_external_id ||
        ('organizer' in data ? data.organizer?.external_id : null) ||
        null,
      location: data.location || '',
      is_all_day: Boolean(data.is_all_day),
      all_day_date: data.start_at
        ? this.dateKeyForTimeZone(data.start_at, timezone)
        : defaults.start.slice(0, 10),
      reminder_minutes: Number(data.reminder_minutes ?? 15),
      category: data.category || 'Reunión',
      recurrence_frequency:
        data.recurrence_pattern?.frequency || 'NONE',
      recurrence_interval: Number(data.recurrence_pattern?.interval || 1),
      recurrence_end_date:
        data.recurrence_pattern?.end_date || '',
    });

    this.participants.clear({ emitEvent: false });
    participants.forEach((participant) =>
      this.participants.push(this.createParticipantGroup(participant)),
    );
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.onMeetingTypeChanged();
    this.form.markAsPristine();

    if (this.isReadOnly || this.isMeetingClosed || this.isSeriesExpired) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
      if (this.isRescheduleOnly) {
        this.form.disable({ emitEvent: false });
        this.form.get('start_at')?.enable({ emitEvent: false });
        this.form.get('end_at')?.enable({ emitEvent: false });
        this.form.get('timezone')?.enable({ emitEvent: false });
      }
    }
  }

  private createParticipantGroup(
    participant: Partial<MeetingParticipantPayload> = {},
  ): FormGroup {
    return this.fb.group({
      external_id: [participant.external_id || null],
      entity_type: [participant.entity_type || null],
      entity_id: [participant.entity_id || null],
      entity_external_id: [participant.entity_external_id || null],
      name: [participant.name || '', Validators.maxLength(255)],
      email: [
        participant.email || '',
        [Validators.required, Validators.email, Validators.maxLength(255)],
      ],
      participant_role: [
        participant.participant_role ||
          (participant.entity_type === 'USER'
            ? this.internalUserParticipantRole
            : this.defaultParticipantRole),
      ],
      attendance_type: [
        participant.attendance_type || 'REQUIRED',
        Validators.required,
      ],
      response_status: [
        (participant as MeetingParticipant).response_status || null,
      ],
      attendance_status: [
        (participant as MeetingParticipant).attendance_status ||
          'NOT_AVAILABLE',
      ],
      first_joined_at: [
        (participant as MeetingParticipant).first_joined_at || null,
      ],
      last_left_at: [
        (participant as MeetingParticipant).last_left_at || null,
      ],
      total_attendance_seconds: [
        (participant as MeetingParticipant).total_attendance_seconds || 0,
      ],
      attendance_synced_at: [
        (participant as MeetingParticipant).attendance_synced_at || null,
      ],
      is_current_user: [
        (participant as MeetingParticipant).is_current_user || false,
      ],
      can_register_attendance: [
        (participant as MeetingParticipant).can_register_attendance || false,
      ],
    });
  }

  private buildPayload(): MeetingPayload {
    const raw = this.form.getRawValue();
    const timezone = String(raw.timezone);
    const descriptionType = raw.description_type as MeetingDescriptionType;
    const rawDescription = String(raw.description || '').trim();
    const description =
      descriptionType === 'HTML'
        ? this.sanitizer.sanitize(SecurityContext.HTML, rawDescription)
        : rawDescription;
    const organizerUserId = raw.organizer_user_id
      ? Number(raw.organizer_user_id)
      : null;
    const organizerUserExternalId = String(
      raw.organizer_user_external_id || '',
    ).trim();
    const isAllDay = Boolean(raw.is_all_day);
    const allDayDate = String(raw.all_day_date || '').slice(0, 10);
    const startAt = isAllDay
      ? fromZonedTime(`${allDayDate}T00:00`, timezone).toISOString()
      : fromZonedTime(String(raw.start_at), timezone).toISOString();
    const allDayEnd = isAllDay
      ? new Date(fromZonedTime(`${allDayDate}T00:00`, timezone))
      : null;
    if (allDayEnd) allDayEnd.setUTCDate(allDayEnd.getUTCDate() + 1);
    const endAt = isAllDay
      ? allDayEnd!.toISOString()
      : fromZonedTime(String(raw.end_at), timezone).toISOString();

    const recurrenceFrequency = String(
      raw.recurrence_frequency || 'NONE',
    ) as 'NONE' | MeetingRecurrenceFrequency;
    const recurrenceEndDate = String(raw.recurrence_end_date || '').slice(0, 10);
    const recurrence =
      recurrenceFrequency === 'NONE'
        ? null
        : {
            frequency: recurrenceFrequency,
            interval: Math.max(Number(raw.recurrence_interval || 1), 1),
            end_date: recurrenceEndDate,
            ...(recurrenceFrequency === 'WEEKLY'
              ? {
                  days_of_week: [
                    this.weekdayCode(
                      isAllDay ? allDayDate : String(raw.start_at).slice(0, 10),
                    ),
                  ],
                }
              : {}),
          };

    return {
      subject: String(raw.subject).trim(),
      description: description || null,
      description_type: descriptionType,
      start_at: startAt,
      end_at: endAt,
      timezone,
      meeting_type: raw.meeting_type as MeetingType,
      provider: raw.provider as MeetingProvider,
      ...(organizerUserId ? { organizer_user_id: organizerUserId } : {}),
      ...(organizerUserExternalId
        ? { organizer_user_external_id: organizerUserExternalId }
        : {}),
      location: String(raw.location || '').trim() || null,
      is_all_day: isAllDay,
      reminder_minutes: Math.max(Number(raw.reminder_minutes || 0), 0),
      category: String(raw.category || '').trim() || null,
      recurrence_pattern: recurrence,
      participants: this.buildParticipantPayloads(),
      links: (this.initialData?.links || this.currentMeeting?.links || []).map(
        (link) => ({
          entity_type: link.entity_type,
          ...(link.entity_id ? { entity_id: Number(link.entity_id) } : {}),
          ...(link.entity_external_id
            ? { entity_external_id: link.entity_external_id }
            : {}),
          relation_type: link.relation_type || null,
        }),
      ),
    };
  }

  private buildParticipantPayloads(): MeetingParticipantPayload[] {
    return this.participants.controls.map((control) => {
      const value = control.getRawValue();
      return {
        ...(value.external_id ? { external_id: value.external_id } : {}),
        entity_type: value.entity_type || null,
        entity_id: value.entity_id ? Number(value.entity_id) : null,
        entity_external_id: value.entity_external_id || null,
        name: String(value.name || '').trim() || null,
        email: String(value.email).trim().toLowerCase(),
        participant_role: value.participant_role || this.defaultParticipantRole,
        attendance_type:
          (value.attendance_type as MeetingAttendanceType) || 'REQUIRED',
      };
    });
  }

  private updateMeeting(payload: MeetingPayload): Observable<Meeting> {
    const externalId = this.resolvedExternalId;
    if (!externalId) {
      throw new Error('No se encontró el identificador de la reunión.');
    }

    return this.meetingService.updateFull(externalId, payload);
  }

  private validateDates(): boolean {
    const raw = this.form.getRawValue();
    try {
      const timezone = String(raw.timezone);
      const isAllDay = Boolean(raw.is_all_day);
      const allDayDate = String(raw.all_day_date || '').slice(0, 10);
      const start = isAllDay
        ? fromZonedTime(`${allDayDate}T00:00`, timezone)
        : fromZonedTime(String(raw.start_at), timezone);
      const end = isAllDay
        ? new Date(start.getTime() + 24 * 60 * 60 * 1000)
        : fromZonedTime(String(raw.end_at), timezone);

      if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) {
        throw new Error('Fecha inválida');
      }
      if (end.getTime() <= start.getTime()) {
        this.errorMessage =
          'La fecha de finalización debe ser posterior a la fecha de inicio.';
        return false;
      }

      const recurrenceFrequency = String(raw.recurrence_frequency || 'NONE');
      if (recurrenceFrequency !== 'NONE') {
        const recurrenceEnd = String(raw.recurrence_end_date || '').slice(0, 10);
        const startDate = isAllDay
          ? allDayDate
          : String(raw.start_at || '').slice(0, 10);
        if (!recurrenceEnd) {
          this.errorMessage = 'Indique hasta qué fecha debe repetirse la serie.';
          return false;
        }
        if (recurrenceEnd < startDate) {
          this.errorMessage =
            'La fecha final de la serie no puede ser anterior al inicio.';
          return false;
        }
      }

      if (this.isRescheduleOnly && end.getTime() <= Date.now()) {
        this.errorMessage =
          'Para reprogramar, la nueva fecha de finalización debe estar en el futuro.';
        return false;
      }
      return true;
    } catch {
      this.errorMessage = 'Ingrese fechas y horas válidas para la reunión.';
      return false;
    }
  }

  private validateParticipants(): boolean {
    const emails = this.participants.controls.map((control) =>
      String(control.get('email')?.value || '')
        .trim()
        .toLowerCase(),
    );
    const duplicated = emails.find(
      (email, index) => email && emails.indexOf(email) !== index,
    );

    if (duplicated) {
      this.participantError =
        'El participante ya fue agregado. Un mismo correo solo puede aparecer una vez, incluso si se escribió manualmente.';
      return false;
    }

    const references = this.participants.controls
      .map((control) => {
        const entityType = String(
          control.get('entity_type')?.value || '',
        ).trim().toUpperCase();
        const entityReference = String(
          control.get('entity_external_id')?.value ||
            control.get('entity_id')?.value ||
            '',
        ).trim().toLowerCase();
        return entityType && entityReference
          ? `${entityType}:${entityReference}`
          : '';
      })
      .filter(Boolean);
    const duplicatedReference = references.find(
      (reference, index) => references.indexOf(reference) !== index,
    );
    if (duplicatedReference) {
      this.participantError =
        'El mismo usuario Zivo no puede agregarse más de una vez a la reunión.';
      return false;
    }

    if (this.participants.invalid) {
      this.participantError =
        'Revise los nombres y correos de los participantes.';
      return false;
    }
    return true;
  }

  private handleRequestError(error: unknown): void {
    const httpError = error as HttpErrorResponse;
    const body = httpError?.error;
    const existingExternalId =
      body?.errors?.existing_meeting_external_id ||
      body?.data?.existing_meeting_external_id ||
      body?.existing_meeting_external_id;

    if (httpError?.status === 409 && existingExternalId) {
      this.conflictExternalId = String(existingExternalId);
      this.errorMessage =
        'Ya existe una reunión activa para este contexto. Puede abrirla y actualizarla.';
      this.conflict.emit({
        existingMeetingExternalId: this.conflictExternalId,
      });
      return;
    }

    this.errorMessage =
      body?.message ||
      httpError?.message ||
      'No se pudo completar la operación. Intente nuevamente.';
  }

  private emitClosed(): void {
    this.meetingRequestSequence += 1;
    this.restoreBodyScroll();
    this.closed.emit({ action: 'closed', meeting: this.currentMeeting });
  }

  private resetVisualState(): void {
    this.loading = false;
    this.saving = false;
    this.attendanceLoading = false;
    this.errorMessage = '';
    this.attendanceMessage = '';
    this.participantError = '';
    this.conflictExternalId = null;
    this.confirmingClose = false;
    this.lifecycleAction = null;
    this.showDescriptionPreview = false;
    this.maximized = false;
    this.userSearch = '';
    this.userSearchOpen = false;
    this.searchingUsers = false;
    this.userSearchError = '';
    this.userSearchResults = [];
    this.userSearchSubject.next('');
    this.form.enable({ emitEvent: false });
  }

  private configureUserSearch(): void {
    this.userSearchSubject
      .pipe(
        map((value) => String(value || '').trim()),
        debounceTime(280),
        switchMap((search) => {
          if (search.length < 2) {
            return of({ users: [] as UsuarioBasico[], error: '' });
          }
          this.searchingUsers = true;
          return this.usuariosService
            .listarBasico({ search, enabled: true, limit: 12 })
            .pipe(
              map((users) => ({ users, error: '' })),
              catchError(() =>
                of({
                  users: [] as UsuarioBasico[],
                  error: 'No se pudieron consultar los usuarios de ZIVO.',
                }),
              ),
            );
        }),
        takeUntil(this.destroySubject),
      )
      .subscribe(({ users, error }) => {
        this.searchingUsers = false;
        this.userSearchResults = users;
        this.userSearchError = error;
      });
  }

  private focusFirstInvalidControl(): void {
    window.setTimeout(() => {
      this.dialogPanel?.nativeElement
        .querySelector<HTMLElement>('.ng-invalid:not(form)')
        ?.focus();
    });
  }

  private scheduleInitialFocus(): void {
    this.clearFocusTimer();
    this.focusTimer = window.setTimeout(() => {
      const selector = this.isReadOnly
        ? '[data-modal-close]'
        : '[data-initial-focus]';
      this.dialogPanel?.nativeElement
        .querySelector<HTMLElement>(selector)
        ?.focus();
      this.focusTimer = null;
    }, 50);
  }

  private focusConfirmation(): void {
    window.setTimeout(() => {
      this.dialogPanel?.nativeElement
        .querySelector<HTMLElement>('.meeting-confirm-overlay button')
        ?.focus();
    });
  }

  private lockBodyScroll(): void {
    if (!this.previouslyFocusedElement) {
      this.previouslyFocusedElement = document.activeElement as HTMLElement | null;
    }
    if (this.previousBodyOverflow === null) {
      this.previousBodyOverflow = document.body.style.overflow;
    }
    document.body.style.overflow = 'hidden';
  }

  private restoreBodyScroll(): void {
    if (this.previousBodyOverflow === null) return;
    document.body.style.overflow = this.previousBodyOverflow;
    this.previousBodyOverflow = null;
    const previousFocus = this.previouslyFocusedElement;
    this.previouslyFocusedElement = null;
    if (previousFocus?.isConnected) {
      window.setTimeout(() => previousFocus.focus());
    }
  }

  private clearFocusTimer(): void {
    if (this.focusTimer === null) return;
    window.clearTimeout(this.focusTimer);
    this.focusTimer = null;
  }

  private toLocalDateTime(value: string, timezone: string): string {
    try {
      return formatInTimeZone(value, timezone, "yyyy-MM-dd'T'HH:mm");
    } catch {
      return '';
    }
  }

  private effectiveStartDateKey(): string {
    const raw = this.form.getRawValue();
    if (raw.is_all_day && raw.all_day_date) {
      return String(raw.all_day_date).slice(0, 10);
    }
    return String(raw.start_at || this.defaultDates().start).slice(0, 10);
  }

  private localDateKey(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private dateKeyForTimeZone(value: string, timezone: string): string {
    try {
      return formatInTimeZone(value, timezone, 'yyyy-MM-dd');
    } catch {
      return String(value || '').slice(0, 10);
    }
  }

  private weekdayCode(dateKey: string):
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY' {
    const date = new Date(`${dateKey}T12:00:00`);
    return [
      'SUNDAY',
      'MONDAY',
      'TUESDAY',
      'WEDNESDAY',
      'THURSDAY',
      'FRIDAY',
      'SATURDAY',
    ][date.getDay()] as
      | 'MONDAY'
      | 'TUESDAY'
      | 'WEDNESDAY'
      | 'THURSDAY'
      | 'FRIDAY'
      | 'SATURDAY'
      | 'SUNDAY';
  }

  private defaultDates(): { start: string; end: string } {
    const start = new Date();
    start.setSeconds(0, 0);
    const minutes = start.getMinutes();
    start.setMinutes(minutes < 30 ? 30 : 60);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const formatLocal = (date: Date): string => {
      const pad = (value: number): string => String(value).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };
    return { start: formatLocal(start), end: formatLocal(end) };
  }

  private normalizeConfiguration(
    configuration: MeetingConfiguration,
  ): MeetingConfiguration {
    const fallback = this.fallbackConfiguration();
    const normalizeOptions = <T extends string>(
      values: unknown,
      defaults: MeetingConfigurationOption<T>[],
    ): MeetingConfigurationOption<T>[] => {
      if (!Array.isArray(values) || values.length === 0) return defaults;
      return values.map((option) => {
        if (typeof option === 'string') {
          return { value: option as T, label: option.replaceAll('_', ' ') };
        }
        return option as MeetingConfigurationOption<T>;
      });
    };

    return {
      providers: normalizeOptions(configuration?.providers, fallback.providers),
      meeting_types: normalizeOptions(
        configuration?.meeting_types,
        fallback.meeting_types,
      ),
      timezones: normalizeOptions(
        configuration?.timezones,
        fallback.timezones,
      ),
      default_provider:
        configuration?.default_provider || fallback.default_provider,
      default_meeting_type:
        configuration?.default_meeting_type || fallback.default_meeting_type,
      default_timezone:
        configuration?.default_timezone || fallback.default_timezone,
    };
  }

  private fallbackConfiguration(): MeetingConfiguration {
    return {
      providers: [
        {
          value: 'MICROSOFT_TEAMS',
          label: 'Microsoft Teams',
          enabled: false,
        },
        {
          value: 'MICROSOFT_OUTLOOK',
          label: 'Microsoft Outlook',
          enabled: false,
        },
        { value: 'GOOGLE_MEET', label: 'Google Meet', enabled: false },
        { value: 'ZOOM', label: 'Zoom', enabled: false },
        { value: 'NONE', label: 'Sin videoconferencia' },
      ],
      meeting_types: [
        { value: 'ONLINE', label: 'Virtual' },
        { value: 'PRESENTIAL', label: 'Presencial' },
        { value: 'HYBRID', label: 'Híbrida' },
      ],
      timezones: [
        { value: 'America/Guayaquil', label: 'Ecuador (Guayaquil)' },
        { value: 'America/Bogota', label: 'Colombia (Bogotá)' },
        { value: 'America/Lima', label: 'Perú (Lima)' },
        { value: 'America/Mexico_City', label: 'México (Ciudad de México)' },
        { value: 'America/New_York', label: 'EE. UU. (Nueva York)' },
        { value: 'Europe/Madrid', label: 'España (Madrid)' },
        { value: 'UTC', label: 'UTC' },
      ],
      default_provider: 'NONE',
      default_meeting_type: 'ONLINE',
      default_timezone: 'America/Guayaquil',
    };
  }
}
